/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import '@gjensidige/nci-core-grid/lib-css/index.css';

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Expandable, Link } from '@gjensidige/builders-components';
import { Container } from '@gjensidige/nci-core-grid/lib';

import { ToCProps as Props } from './types';

import styles from '../less/toc.module.less';

export const ToC = ({ title, list }: Props) => {
  const [activeUrl, setActiveUrl] = useState('');
  const [width, setWidth] = useState(null);
  const [openMobile, setOpenMobile] = useState(false);
  const [openDesktop, setOpenDesktop] = useState(true);

  const isMobile = width < 800;
  const handleResize = () => setWidth(window.innerWidth);

  useIntersectionObserver(list, setActiveUrl);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      setWidth(window.innerWidth);

      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return (
    <div className={styles.tocOuter}>
      <Container>
        <div
          className={classNames(
            styles.tocInner,
            !openDesktop && styles.tocInnerClosed
          )}
        >
          <Expandable
            open={isMobile ? openMobile : openDesktop}
            onChange={() => {
              setOpenMobile(!openMobile);
              setOpenDesktop(!openDesktop);
            }}
            variant="line-white-yellow"
            className={styles.tocExpandable}
          >
            <Expandable.Title className={styles.tocTitle}>
              {title}
            </Expandable.Title>
            <Expandable.Content className={styles.tocContent}>
              {!list?.length || (
                <ul>
                  {list.map((item, index) => (
                    <li
                      key={`${item.content}_${index}`}
                      onClick={() => {
                        setOpenMobile(false);
                        setOpenDesktop(false);
                      }}
                    >
                      <Link
                        className={
                          activeUrl === item.url ? styles.active : null
                        }
                        href={item.url}
                      >
                        {item.content}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Expandable.Content>
          </Expandable>
        </div>
      </Container>
    </div>
  );
};

ToC.CSR = true;

export default ToC;

function useIntersectionObserver(
  list: Props['list'],
  setActiveUrl: React.Dispatch<React.SetStateAction<string>>
) {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return; // Skip if no support
    const observer = new IntersectionObserver(
      (entries) =>
        Array.from(entries)
          .sort((a, b) => a.intersectionRatio - b.intersectionRatio)
          .find((entry) => {
            if (entry.isIntersecting) setActiveUrl(`#${entry.target.id}`);
          }),
      { threshold: 0.8 }
    );

    // Observe all sections
    list.forEach(({ url }) => {
      const section = document.getElementById(url.slice(1));
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, [list, setActiveUrl]);
}
